import React from 'react';

import * as customPropTypes from '@/custom-prop-types';
import { Typeahead as OriginalTypeahead } from '@/design-system/atoms/Forms/Typeahead';
import { LazyIconSearch } from '@/design-system/atoms/Icons/IconSearch/Lazy';
import { trackInteraction } from '@/utils/analytics';

import { useTermFinderContext } from '../../stores/termFinderStore';
import styles from './Typeahead.module.scss';

const emptyArray = [];

export const Typeahead = ({ analytics }) => {
    const label = useTermFinderContext((s) => s.uiCopy.label);
    const term = useTermFinderContext((s) => s.term);
    const initialTerm = useTermFinderContext((s) => s.initialTerm);
    const lastSearchTerm = useTermFinderContext((s) => s.lastSearchTerm);
    const onTermChange = useTermFinderContext((s) => s.onTermChange);
    const suggestions = useTermFinderContext((s) => s.suggestions.data ?? emptyArray);
    const onSearchTerm = useTermFinderContext((s) => s.onSearchTerm);
    const onSelectedSuggestionChange = useTermFinderContext((s) => s.onSelectedSuggestionChange);
    const onUpdateLastSearch = useTermFinderContext((s) => s.onUpdateLastSearch);

    const handleSubmit = React.useCallback(
        (e) => {
            e.preventDefault();
            onUpdateLastSearch(term);
            if (term?.trim() !== '' && term !== lastSearchTerm) {
                onSearchTerm(term);
                if (analytics) {
                    trackInteraction({
                        ...analytics,
                        searchTerm: term,
                        interactionType: 'search_results',
                    });
                }
            }
        },
        [onSearchTerm, analytics, onUpdateLastSearch, lastSearchTerm, term],
    );

    const handleSelectionItemChange = React.useCallback(
        (item) => {
            onSelectedSuggestionChange(item?.text ?? '');
            if (analytics && item?.text) {
                trackInteraction({
                    ...analytics,
                    actionLabel: item.text,
                    searchTerm: item.text,
                    interactionType: 'predictive_search_click',
                });
            }
        },
        [onSelectedSuggestionChange, analytics],
    );

    return (
        <form
            onSubmit={handleSubmit}
            className={styles['term-finder__input']}
            data-trigger={analytics ? analytics?.selector || 'search' : undefined}
        >
            <OriginalTypeahead
                label={label}
                buttonProps={{
                    type: 'submit',
                    iconComponent: LazyIconSearch,
                    ariaLabel: term ? `Submit search for ${term}` : 'Submit Search',
                }}
                items={suggestions}
                inputValue={term}
                defaultInputValue={initialTerm}
                onInputChange={onTermChange}
                onSelectionItemChange={handleSelectionItemChange}
            />
        </form>
    );
};
Typeahead.propTypes = {
    /**
     * analytics object
     */
    analytics: customPropTypes.analyticsPropType,
};
